import { h } from 'vue'
import { configureDictation, withVariant } from '@addictivegroup/addictive-dictation'
import i18n, { te } from '@/i18n/index.js'
import { api } from '@/lib/http.js'
import { formatBytes } from '@/lib/format.js'
import { useMeta } from '@/stores/meta.js'
import { useUi } from '@/stores/ui.js'
import { useSession } from '@/stores/session.js'

/**
 * Dictation in AddCRM (D-143…D-146, D-180), through addictive-dictation: the device's storage
 * under the panel's old names (models already downloaded stay), the fast model from the panel's
 * own server, the accurate one from the company's copy when the image says so; the company's
 * rules and glossary from the configuration (/meta), lessons to /speech-terms/learn; the
 * panel's toasts and dialogs; its own words where they name its pages (i18n `dictation.*`);
 * its own look — the parts drawn with its classes and Font Awesome, as before the library.
 */
const FA = { mic: 'fa-microphone', stop: 'fa-stop', phone: 'fa-mobile-screen', download: 'fa-download', trash: 'fa-trash-can', lock: 'fa-building-lock', spinner: 'fa-circle-notch fa-spin', warn: 'fa-triangle-exclamation' }
const LIVE = 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
export const DICTATION_CLASSES = {
  fieldBox: 'group/dictate relative',
  mic: ({ live, starting, position }) => [
    'absolute right-1.5 inline-flex h-7 w-7 items-center justify-center rounded-md text-sm transition-opacity',
    { top: 'top-1', bottom: 'bottom-1.5', middle: 'top-1/2 -translate-y-1/2' }[position],
    live ? 'bg-red-50 text-red-600 opacity-100 dark:bg-red-950/50' : starting ? 'text-muted opacity-100' : 'text-faint opacity-0 hover:bg-[var(--surface-3)] hover:text-[var(--text-1)] group-hover/dictate:opacity-100 group-focus-within/dictate:opacity-100 pointer-coarse:opacity-100',
  ].join(' '),
  // Red only while the sound reaches the recognition; grey while the microphone is starting.
  status: ({ field, starting }) => `${field ? 'mt-1 rounded-md' : 'rounded-lg'} flex items-center gap-2 px-2 py-1 text-xs ${starting ? 'bg-[var(--surface-3)] text-muted' : LIVE}`,
  statusDot: ({ starting }) => (starting ? 'h-2 w-2 shrink-0 rounded-full bg-gray-400' : 'h-2 w-2 shrink-0 animate-pulse rounded-full bg-red-600'),
  statusText: 'min-w-0 flex-1',
  statusInterim: 'flex min-w-0 flex-1 justify-end overflow-hidden whitespace-nowrap italic',
  language: 'input w-auto py-1 text-sm',
  statusLocal: 'shrink-0 rounded bg-red-100 px-1.5 py-0.5 text-3xs font-medium dark:bg-red-900/50',
}
export function setupDictation() {
  const meta = () => {
    try {
      return useMeta()
    } catch {
      return null // no store yet: no rules, no glossary
    }
  }
  configureDictation({
    prefix: 'addcrm',
    locale: () => i18n.global.locale.value,
    // The language one dictates in: chosen in one's settings (D-338), else the interface's.
    language: () => {
      let chosen = null
      try {
        chosen = useSession().me?.settings?.dictation_language
      } catch {
        // no store yet
      }
      return chosen || (i18n.global.locale.value === 'en' ? 'en-GB' : 'pl-PL')
    },
    ...(import.meta.env.VITE_SPEECH_MODEL_BASE ? { accurateBase: import.meta.env.VITE_SPEECH_MODEL_BASE } : {}),
    policy: () => meta()?.data?.settings?.speech || {},
    terms: () => meta()?.data?.speech_terms || [],
    learn: (lesson) => api('/speech-terms/learn', { method: 'POST', body: lesson }),
    onLearned: (term, heard) => {
      const m = meta()
      if (m?.data) m.data.speech_terms = withVariant(m.data.speech_terms, term, heard)
    },
    notify: (kind, message) => (kind === 'info' ? useUi().toast(message, { kind: 'info' }) : useUi()[kind](message)),
    confirm: ({ title, message, confirmLabel, danger }) => useUi().ask({ title, message, confirmLabel, danger }),
    formatSize: formatBytes,
    classes: DICTATION_CLASSES,
    icon: (name) => h('i', { class: ['fa-solid', FA[name]], 'aria-hidden': 'true' }),
    translate: (key, params) => (te(`dictation.${key}`) ? i18n.global.t(`dictation.${key}`, params || {}) : null),
  })
}
